/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../buttons/Button";
import { errorAlert, makeApiRequest, successAlert } from "../../util";
import AuthContext from "../../store/authContext";
import { ADD_LMR_USERS_FORM_FIELDS } from "../../constants";
import Form from "../../ui/Form/Form";
import TextField from "../inputs/TextField";
import { ILMRUserData } from "../../types";
import DeleteModal, { DeleteModalIcons } from "../modal/DeleteModal";
import { LMRUsersAdd } from "../../api/icCheck";
import {
  CREATE_LMR_USER_TRANSLATIONS,
  FORM_VALIDATION_TRANSLATIONS,
} from "../../translations/util";
import ToggleField from "../inputs/ToggleField";

interface IProps {
  org: string;
}
const set = new Set();

const isFormInvalid = ({ data }: { data: any }) => {
  if (!data) return true;

  const isConnectionFormInvalid = ADD_LMR_USERS_FORM_FIELDS.map((rows: any) => {
    const res = rows.fields.map((field: any) => {
      switch (field.type) {
        case "TEXT":
          return field.isInputInvalid?.(data[field.name]);
        default:
          return false;
      }
    });

    return res.reduce((acc: any, el: any) => acc || el);
  }).reduce((acc: any, el: any) => acc || el);
  return isConnectionFormInvalid;
};

const NewLMRUser = ({ org }: IProps) => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    set.clear();
    return () => {
      set.clear();
    };
  }, []);

  const [connectionData, setConnectionData] = useState<ILMRUserData>({
    "LMR-ID": undefined,
    "LMR-NW-TYPE": undefined,
    "MAPPED-MCPTT-ID": undefined,
    "DISPLAY-NAME": undefined,
    "MUTUAL-AID": false,
  });

  const createLMRUser = async () => {
    setLoading(true);
    try {
      await LMRUsersAdd({
        data: JSON.stringify({
          "LMR-ID": connectionData["LMR-ID"],
          "MAPPED-MCPTT-ID": connectionData["MAPPED-MCPTT-ID"],
        }),
        org,
        token,
      });

      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/lmr-users`,
        method: "POST",
        body: JSON.stringify({
          "LMR-ID": connectionData["LMR-ID"],
          "LMR-NW-TYPE": connectionData["LMR-NW-TYPE"],
          "MAPPED-MCPTT-ID": connectionData["MAPPED-MCPTT-ID"],
          "DISPLAY-NAME": connectionData["DISPLAY-NAME"],
          "MUTUAL-AID": connectionData["MUTUAL-AID"],
        }),
        token,
        org,
      });
      successAlert(CREATE_LMR_USER_TRANSLATIONS.successAlertMessage);
      navigate("/configurationManagement/lmrUsers");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="data-form">
        <Form>
          <div className="data-tabcontent data-card">
            <div className="grid grid-cols-1 gap-4 form-control single-item">
              {ADD_LMR_USERS_FORM_FIELDS.map(
                (rows: {
                  cols: number;
                  fields: {
                    name: keyof ILMRUserData;
                    tooltipText?: string;
                    label: string;
                    invalidText?: string | null;
                    isInputInvalid?: (
                      val?: string | string[],
                      data?: any
                    ) => boolean;
                    required?: boolean;
                    type: string;
                    placeholder: string[];
                  }[];
                }) => {
                  return (
                    <div
                      className={`grid grid-cols-${rows.cols} gap-4 form-control single-item`}
                    >
                      {rows.fields.map((field) => {
                        switch (field.type) {
                          case "TOGGLE":
                            return (
                              <ToggleField
                                labelText={field.label}
                                onChange={(val) =>
                                  setConnectionData({
                                    ...connectionData,
                                    [field.name]: val,
                                  })
                                }
                                value={
                                  connectionData[
                                    field.name
                                  ] as unknown as boolean
                                }
                                tooltipText={field.tooltipText}
                              />
                            );
                          default:
                            return (
                              <TextField
                                onChange={(val) => {
                                  set.add(field.name);
                                  setConnectionData({
                                    ...connectionData,
                                    [field.name]: val,
                                  });
                                }}
                                placeholder={field.label}
                                value={connectionData[field.name] as string}
                                invalid={
                                  set.has(field.name) &&
                                  field.isInputInvalid?.(
                                    connectionData[field.name] as string
                                  )
                                }
                                invalidText={
                                  field.invalidText ||
                                  FORM_VALIDATION_TRANSLATIONS.required(
                                    field.label
                                  )
                                }
                                labelText={field.label}
                                tooltipText={field.tooltipText}
                                required={field.required}
                              />
                            );
                        }
                      })}
                    </div>
                  );
                }
              )}
            </div>
          </div>
        </Form>
      </div>
      <DeleteModal
        modalHeaderText={CREATE_LMR_USER_TRANSLATIONS.deleteModal.header}
        modalDescriptionText={
          CREATE_LMR_USER_TRANSLATIONS.deleteModal.description
        }
        onDelete={() => navigate("/configurationManagement/lmrUsers")}
        open={open}
        setOpen={setOpen}
        confirmationButtonLabel={
          CREATE_LMR_USER_TRANSLATIONS.deleteModal.goBackButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <div className="flex justify-end form-footer">
        <div className="mr-2">
          <Button
            className="w-full"
            type="button"
            kind="tertiary"
            disabled={false}
            label={CREATE_LMR_USER_TRANSLATIONS.cancelButtonLabel}
            onClick={() => setOpen(true)}
            dataCy="cancelCreateLMRUser-"
          />
        </div>

        <div>
          <Button
            className="w-full"
            type="button"
            loading={loading}
            disabled={isFormInvalid({ data: connectionData })}
            onClick={createLMRUser}
            label={CREATE_LMR_USER_TRANSLATIONS.createButtonLabel}
            dataCy="createLMRUser-"
          />
        </div>
      </div>
    </>
  );
};

export default NewLMRUser;
